import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api, { refreshAccessToken } from "../../api/axiosInstance";

// ── THUNKS ────────────────────────────────────────────────────────────────────
export const loginUser = createAsyncThunk(
  "auth/login",
  async (credentials, { rejectWithValue }) => {
    try {
      const { data } = await api.post("/auth/login", credentials);
      return data.data; // { accessToken, user }
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Login failed");
    }
  },
);

// Shared by both "Continue with Google" and "Continue with Apple" — same
// request/response shape as loginUser ({ accessToken, user }), just a
// different endpoint per provider, so it reuses loginUser's exact
// pending/fulfilled/rejected reducer cases below rather than duplicating
// them.
export const socialAuth = createAsyncThunk(
  "auth/social",
  async ({ provider, ...body }, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`/auth/${provider}`, body);
      return data.data; // { accessToken, user }
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Sign-in failed");
    }
  },
);

export const registerUser = createAsyncThunk(
  "auth/register",
  async (formData, { rejectWithValue }) => {
    try {
      const { data } = await api.post("/auth/register", formData);
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Registration failed",
      );
    }
  },
);

export const logoutUser = createAsyncThunk(
  "auth/logout",
  async (_, { dispatch }) => {
    // Clear client state FIRST, synchronously (before the network call/
    // await below) — dispatching a plain action runs immediately when this
    // thunk starts, so by the time callers' `navigate("/login")` (called
    // right after `dispatch(logoutUser())`) actually runs, Redux already
    // says logged-out. Doing this the other way around (server call first,
    // clear state after) is what caused a dashboard->login->dashboard->
    // login flash: navigating to /login while isAuthenticated was still
    // stale-true bounced the user back via RedirectIfAuthenticated, then
    // bounced again once state actually cleared a moment later. The actual
    // server-side cookie/DB cleanup doesn't need to block the UI at all.
    dispatch(logout());
    try {
      await api.post("/auth/logout");
    } catch (_) {}
  },
);

// ─────────────────────────────────────────────
// INIT AUTH (MAIN BOOTSTRAP)
// ─────────────────────────────────────────────
export const initAuth = createAsyncThunk(
  "auth/init",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      // Goes through the shared refreshAccessToken() (also used by the
      // response interceptor's 401 handling) instead of calling
      // /auth/refresh directly, so this and any concurrent interceptor-
      // triggered refresh always share one in-flight request rather than
      // racing the server's refresh-token rotation.
      await refreshAccessToken();

      // Fetch user safely
      const meRes = await api.get("/auth/me");

      return meRes.data.data.user;
    } catch (err) {
      dispatch(logout());
      return rejectWithValue("Auth failed");
    }
  },
);

// ── SLICE ─────────────────────────────────────────────────────────────────────
// A multi-TIN customer always operates under exactly one TIN at a time —
// there's no "all TINs" view — so every shipment/consolidation/etc. request
// scopes to this value. Persisted per-account in localStorage so it survives
// a refresh.
const ACTIVE_TIN_STORAGE_PREFIX = "waypoint_active_tin_";

// Resolves the active TIN for a freshly (re)loaded user, and detects the
// case where the stored choice no longer belongs to the account (e.g. an
// admin removed it) — the caller surfaces `resetNotice` as a toast so the
// silent-fallback isn't invisible to the customer.
const resolveActiveTin = (user) => {
  if (!user?._id || !user?.canManageMultipleTins) return { tin: null, resetNotice: null };
  const tins = user.tins || [];
  if (tins.length === 0) return { tin: null, resetNotice: null };

  let stored = null;
  try {
    stored = localStorage.getItem(ACTIVE_TIN_STORAGE_PREFIX + user._id);
  } catch (_) {}

  if (stored && tins.some((t) => t.tin === stored)) {
    return { tin: stored, resetNotice: null };
  }

  const fallback = tins[0].tin;
  try {
    localStorage.setItem(ACTIVE_TIN_STORAGE_PREFIX + user._id, fallback);
  } catch (_) {}
  return {
    tin: fallback,
    resetNotice: stored
      ? "Your previously selected TIN is no longer on your account — switched to your primary TIN."
      : null,
  };
};

const authSlice = createSlice({
  name: "auth",
  initialState: {
    user: null,
    accessToken: null,
    loading: false,
    error: null,
    initialized: false,
    activeTin: null,
    activeTinResetNotice: null,
  },
  reducers: {
    setCredentials: (state, action) => {
      const { accessToken, user } = action.payload;
      if (accessToken) state.accessToken = accessToken;
      if (user) {
        state.user = user;
        const { tin, resetNotice } = resolveActiveTin(user);
        state.activeTin = tin;
        state.activeTinResetNotice = resetNotice;
      }
    },
    logout: (state) => {
      state.user = null;
      state.accessToken = null;
      state.error = null;
      state.activeTin = null;
      state.activeTinResetNotice = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    setActiveTin: (state, action) => {
      state.activeTin = action.payload;
      if (state.user?._id) {
        try {
          localStorage.setItem(
            ACTIVE_TIN_STORAGE_PREFIX + state.user._id,
            action.payload,
          );
        } catch (_) {}
      }
    },
    clearActiveTinResetNotice: (state) => {
      state.activeTinResetNotice = null;
    },
  },
  extraReducers: (builder) => {
    // login
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.accessToken = action.payload.accessToken;
        state.user = action.payload.user;
        const { tin, resetNotice } = resolveActiveTin(action.payload.user);
        state.activeTin = tin;
        state.activeTinResetNotice = resetNotice;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // social (google/apple) — identical shape/handling to login
    builder
      .addCase(socialAuth.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(socialAuth.fulfilled, (state, action) => {
        state.loading = false;
        state.accessToken = action.payload.accessToken;
        state.user = action.payload.user;
        const { tin, resetNotice } = resolveActiveTin(action.payload.user);
        state.activeTin = tin;
        state.activeTinResetNotice = resetNotice;
      })
      .addCase(socialAuth.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // register
    builder
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // initAuth
    builder
      .addCase(initAuth.pending, (state) => {
        state.loading = true;
      })
      .addCase(initAuth.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.initialized = true;
        const { tin, resetNotice } = resolveActiveTin(action.payload);
        state.activeTin = tin;
        state.activeTinResetNotice = resetNotice;
      })
      .addCase(initAuth.rejected, (state) => {
        state.loading = false;
        state.initialized = true;
      });
  },
});

export const { setCredentials, logout, clearError, setActiveTin, clearActiveTinResetNotice } =
  authSlice.actions;

// Selectors
export const selectCurrentUser = (state) => state.auth.user;
export const selectAuthLoading = (state) => state.auth.loading;
export const selectAuthError = (state) => state.auth.error;
export const selectIsAuthenticated = (state) => !!state.auth.accessToken;
export const selectActiveTin = (state) => state.auth.activeTin;
export const selectActiveTinResetNotice = (state) => state.auth.activeTinResetNotice;

export default authSlice.reducer;
